import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { SessionStatus } from './SessionStatus';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Login = () => {
  const { user, login, loading: checkingSession, authError, retryAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Check if session expired (redirected from 401 interceptor)
  const isSessionExpired = window.location.search.includes('expired=1');

  if (checkingSession || authError) return <SessionStatus error={authError} onRetry={retryAuth} />;
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (!result.success) {
      setLoading(false);
    }
    // If success, the AuthProvider will handle navigation automatically
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header with Logo */}
        <div className="text-center fade-in">
          <div className="mx-auto mb-6">
            <img 
              src="/patricks-logo.png" 
              alt="Patrick's Edu Care" 
              className="mx-auto h-32 w-auto object-contain"
            />
          </div>
          <p className="text-gray-600 text-lg font-medium">Student Admission CRM Portal</p>
          {isSessionExpired && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="session-expired-msg">
              <p className="text-amber-700 text-sm">Your session has expired. Please log in again.</p>
            </div>
          )}
        </div>

        {/* Login Card */}
        <Card className="fade-in card-shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <CardTitle className="text-2xl font-semibold text-gray-800">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-gray-600">
              Sign in to access your CRM dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </Label>
                <div className="relative">
                  <i className="fas fa-envelope absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs z-10"></i>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-20 h-12 form-input bg-white/90 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <i className="fas fa-lock absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs z-10"></i>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-20 h-12 form-input bg-white/90 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Signing In...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt mr-2"></i>
                    Sign In
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 fade-in space-y-2">
          <a href="/student-login" data-testid="student-login-link" className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
            <i className="fas fa-graduation-cap mr-1"></i>Student Portal Login
          </a>
          <p>© 2024 Patrick's Edu Care Pvt Ltd. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
